// ============================================
// Qorder Frontend - TypeScript Type Definitions
// ============================================

// Import customization types
import type { ProductCustomizationOptions, OrderItemCustomizations } from './customization'
export * from './customization'

export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  OWNER = 'OWNER',
  MANAGER = 'MANAGER',
  STAFF = 'STAFF',
  KITCHEN = 'KITCHEN',
}

export enum PlanType {
  FREE = 'FREE',
  STARTER = 'STARTER',
  PRO = 'PRO',
  ENTERPRISE = 'ENTERPRISE',
}

export enum OrderStatus {
  CART = 'CART',
  SUBMITTED = 'SUBMITTED',
  PREPARING = 'PREPARING',
  READY = 'READY',
  SERVED = 'SERVED',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
}

export enum PaymentMethod {
  STRIPE = 'STRIPE',
  SATISPAY = 'SATISPAY',
  CASH = 'CASH',
  PENDING = 'PENDING',
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export enum TableStatus {
  AVAILABLE = 'AVAILABLE',
  OCCUPIED = 'OCCUPIED',
  RESERVED = 'RESERVED',
}

export enum SessionStatus {
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

// ============================================
// User & Auth
// ============================================

export interface User {
  id: string
  email: string
  firstName?: string
  lastName?: string
  fullName?: string
  role: UserRole
  restaurantId: string
  restaurantName?: string
  createdAt: string
}

export interface AuthResponse {
  accessToken: string
  refreshToken: string
  user: User
}

export interface LoginRequest {
  email: string
  password: string
}

export interface SignupRequest {
  email: string
  password: string
  firstName: string
  lastName: string
  restaurantName: string
  address: string
  phone: string
}

export interface RefreshTokenRequest {
  refreshToken: string
}

// ============================================
// Restaurant
// ============================================

export type MenuViewMode = 'grid3' | 'grid4' | 'list'
export type MenuThemeId = 'classic' | 'luxury' | 'neon' | 'rustic' | 'ocean' | 'christmas'
export type BackgroundPattern = 'radial' | 'marble' | 'grid' | 'wood' | 'waves' | 'snow'
export type AnimationStyle = 'smooth' | 'elegant' | 'energetic' | 'gentle' | 'flowing' | 'magical'
export type CardStyle = 'modern' | 'luxury' | 'neon' | 'rustic' | 'glass' | 'festive'
export type DecorativeElement = 'orbs' | 'sparkles' | 'scanlines' | 'leaves' | 'bubbles' | 'snowflakes'

export interface MenuTheme {
  id: MenuThemeId
  name: string
  description: string
  emoji: string
  // Core colors
  primaryColor: string
  secondaryColor: string
  accentColor: string
  backgroundColor: string
  cardBackground: string
  cardBackgroundHover: string
  textColor: string
  textSecondary: string
  mutedColor: string
  borderColor: string
  // Gradients
  heroGradient: string
  cardGradient: string
  buttonGradient: string
  // Visual effects
  glowColor: string
  shadowColor: string
  // Background
  backgroundPattern: BackgroundPattern
  backgroundPatternOpacity: number
  // Animation
  animationStyle: AnimationStyle
  // Card style
  cardStyle: CardStyle
  cardBorderRadius: string
  // Decorations
  hasParticles: boolean
  hasFloatingElements: boolean
  decorativeElement: DecorativeElement
}

export interface SocialLinks {
  instagramUrl?: string
  facebookUrl?: string
  tiktokUrl?: string
  websiteUrl?: string
  whatsappNumber?: string
}

export interface MenuLayoutSettings {
  defaultViewMode: MenuViewMode
  showProductBadges: boolean
  showPreparationTime: boolean
  showAllergens: boolean
}

export interface Restaurant {
  id: string
  name: string
  slug: string
  address: string
  phone: string
  plan: PlanType
  stripeAccountId?: string
  currency: string
  defaultLanguage?: string
  supportedLanguages?: string[]
  logoUrl?: string
  createdAt: string
  // Menu Customization
  menuTheme?: MenuThemeId
  defaultViewMode?: MenuViewMode
  showProductBadges?: boolean
  showPreparationTime?: boolean
  showAllergens?: boolean
  // Social Links
  instagramUrl?: string
  facebookUrl?: string
  tiktokUrl?: string
  websiteUrl?: string
  whatsappNumber?: string
}

// ============================================
// Menu
// ============================================

export interface Category {
  id: string
  restaurantId: string
  name: string
  displayOrder: number
  icon?: string
  translations?: Record<string, { name: string }>
  createdAt: string
}

export interface Product {
  id: string
  restaurantId: string
  categoryId: string
  name: string
  description?: string
  price: number
  imageUrl?: string
  allergens: string[]
  tags: string[]
  available: boolean
  preparationTime?: number
  translations?: Record<string, { name: string; description?: string }>
  // Product customization options (for build-your-own products)
  customizationOptions?: ProductCustomizationOptions | null
  // Badge flags for smart recommendations
  isPopular?: boolean
  isNew?: boolean
  isChefRecommended?: boolean
  isVegetarian?: boolean
  isVegan?: boolean
  createdAt: string
  updatedAt: string
}

// ============================================
// Tables & Sessions
// ============================================

export interface RestaurantTable {
  id: string
  restaurantId: string
  tableNumber: string
  location?: string
  capacity?: number
  qrCodeData?: string // Base64 data URL
  qrCodeUrl?: string // The URL encoded in QR
  active: boolean
  notes?: string
  createdAt: string
  updatedAt: string
}

export interface Table {
  id: string
  restaurantId: string
  tableCode: string
  qrCodeUrl: string
  capacity: number
  status: TableStatus
  createdAt: string
}

export interface TableSession {
  id: string
  tableId: string
  restaurantId: string
  sessionCode: string
  totalCustomers: number
  status: SessionStatus
  createdAt: string
  completedAt?: string
}

// ============================================
// Orders
// ============================================

export interface OrderItem {
  id: string
  orderId: string
  productId: string
  product?: Product
  quantity: number
  unitPrice: number
  customizations?: OrderItemCustomizations | null
  itemTotal: number
}

export interface Order {
  id: string
  tableSessionId: string
  customerId: string
  customerName?: string
  restaurantId: string
  status: OrderStatus
  items: OrderItem[]
  subtotal: number
  tax: number
  total: number
  paymentMethod: PaymentMethod
  paymentStatus: PaymentStatus
  paymentIntentId?: string
  notes?: string
  createdAt: string
  updatedAt: string
}

export interface CreateOrderRequest {
  sessionId: string
  customerId: string
  items: {
    productId: string
    quantity: number
    customizations?: Record<string, any>
  }[]
  notes?: string
}

// ============================================
// Payments
// ============================================

export interface Payment {
  id: string
  orderId: string
  tableSessionId: string
  provider: PaymentMethod
  transactionId?: string
  amount: number
  status: PaymentStatus
  metadata?: Record<string, any>
  createdAt: string
}

export interface CreatePaymentIntentRequest {
  orderId: string
}

export interface CreatePaymentIntentResponse {
  clientSecret: string
  publishableKey: string
}

// ============================================
// Analytics
// ============================================

export interface RevenueStats {
  total: number
  trend: number // percentage change
  period: 'today' | 'week' | 'month'
}

export interface TopProduct {
  productId: string
  name: string
  count: number
  revenue: number
}

export interface PeakHour {
  hour: number
  orders: number
}

export interface AnalyticsOverview {
  revenueToday: RevenueStats
  ordersToday: number
  avgOrderValue: number
  tablesOccupied: number
  topProducts: TopProduct[]
  peakHours: PeakHour[]
}

// ============================================
// WebSocket Messages
// ============================================

export interface WebSocketMessage<T = any> {
  type: 'ORDER_CREATED' | 'ORDER_UPDATED' | 'ORDER_STATUS_CHANGED'
  payload: T
}

// ============================================
// API Responses
// ============================================

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}
